import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Sparkles, WifiOff, X as XIcon } from 'lucide-react';
import { AppHeader } from '../components/AppHeader';
import { FormRenderer } from '../components/FormRenderer';
import { Hint } from '../components/Hint';
import { api, getAccessToken } from '../lib/api';
import { useAuth } from '../lib/auth';
import { enqueueLead } from '../lib/db';
import { FormDefinition, LeadFormValues } from '../lib/forms';
import { isOnline } from '../lib/sync';

// Sprint 22 (S22.1/S22.2) · Sprint 40 · OCR-Response mit per-field confidence
interface OcrResponse {
  extracted: Record<string, string>;
  confidence?: number;
  field_confidences?: Record<string, number>;
}

// Sprint 22 (S22.2) · Mapping OCR-Field → Form-Field-IDs
const OCR_FIELD_MAP: Record<string, string> = {
  name: 'name',
  firma: 'firma',
  email: 'email',
  telefon: 'telefon',
  // Sprint 62 · mobil-Mapping ergaenzt damit OCR den Wert ins mobil-Feld setzt
  mobil: 'mobil',
  funktion: 'funktion',
  webseite: 'webseite',
  adresse: 'adresse',
};

// Sprint 40 · Labels für OCR-Result-Modal
const OCR_FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  vorname: 'Vorname',
  nachname: 'Nachname',
  firma: 'Firma',
  funktion: 'Funktion',
  email: 'E-Mail',
  // Sprint 62 · 'Telefon' → 'Festnetz' damit OCR-Result-Modal das richtige Label zeigt
  telefon: 'Festnetz',
  mobil: 'Mobilfunk',
  website: 'Website',
  webseite: 'Webseite',
  adresse: 'Adresse',
};

interface CreatedLead {
  id: string;
  visitenkarte_url: string | null;
}

interface ExtraAttachment {
  file: File;
  kind: 'sketch' | 'foto' | 'dokument';
  preview: string | null;
}

export function Capture() {
  const { user } = useAuth();
  const [form, setForm] = useState<FormDefinition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<LeadFormValues>({});
  const [busy, setBusy] = useState(false);
  const [createdLead, setCreatedLead] = useState<CreatedLead | null>(null);
  const [queuedNotice, setQueuedNotice] = useState<string | null>(null);
  const [cardFile, setCardFile] = useState<File | null>(null);
  const [cardPreview, setCardPreview] = useState<string | null>(null);
  const [extras, setExtras] = useState<ExtraAttachment[]>([]);
  // Sprint 18 (S18.2 + S18.4) · Offline-Indicator + Magic-Sparkle on save-button
  const [online, setOnline] = useState<boolean>(() => isOnline());
  const [magicApplied, setMagicApplied] = useState<boolean>(false);
  const cardInputRef = useRef<HTMLInputElement | null>(null);
  // Sprint 22 (S22.1/S22.2) · OCR-Auto-Trigger State
  const [cardOcrBusy, setCardOcrBusy] = useState<boolean>(false);
  const [cardOcrError, setCardOcrError] = useState<string | null>(null);
  const [ocrAppliedFields, setOcrAppliedFields] = useState<string[]>([]);
  const [ocrConfidence, setOcrConfidence] = useState<number | null>(null);
  const [ocrBannerDismissed, setOcrBannerDismissed] = useState<boolean>(false);
  // Sprint 40 · OCR-Result-Modal · zeigt was OpenAI extrahiert hat,
  // user akzeptiert oder verwirft. Statt silent-auto-fill.
  const [ocrResult, setOcrResult] = useState<OcrResponse | null>(null);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    api<FormDefinition>('/api/forms/active')
      .then((f) => {
        setForm(f);
        // Sprint 61 · Default-Werte fuer ALLE field-types (vorher nur boolean)
        const initial: LeadFormValues = {};
        for (const field of f.schema.fields) {
          if (field.type === 'boolean' || field.type === 'checkbox') {
            initial[field.id] = field.default ?? false;
          } else if (field.default !== undefined && field.default !== null && field.default !== '') {
            initial[field.id] = field.default;
          }
        }
        setValues(initial);
      })
      .catch((err) => setError((err as { detail?: string }).detail ?? 'Fehler'));
  }, []);

  // Cleanup ObjectURLs on unmount or change
  useEffect(() => {
    return () => {
      if (cardPreview) URL.revokeObjectURL(cardPreview);
      extras.forEach((e) => e.preview && URL.revokeObjectURL(e.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sprint 50 · stable callbacks für FormRenderer · iPad-Tipp-Lag-Fix.
  // Verhindert pro-Tastendruck-Rerender im VoiceMagicTextarea (memo greift
  // nur wenn props gleiche Reference behalten).
  const handleFormChange = useCallback((id: string, v: unknown) => {
    setOcrAppliedFields((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev,
    );
    setValues((s) => ({ ...s, [id]: v }));
  }, []);
  const handleMagicAccepted = useCallback(() => {
    setMagicApplied(true);
  }, []);
  // Sprint 50 · localAttachments memoizen · sonst neue Array-Reference pro
  // Render → React.memo um VoiceMagicTextarea greift nicht.
  const memoizedLocalAttachments = useMemo(
    () =>
      extras.map((e) => ({
        file: e.file,
        preview: e.preview,
        isImage: e.file.type.startsWith('image/'),
      })),
    [extras],
  );

  function handleCardChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (cardPreview) URL.revokeObjectURL(cardPreview);
    setCardFile(f);
    setCardPreview(f ? URL.createObjectURL(f) : null);
    // Sprint 22 (S22.1) · Auto-trigger OCR on capture
    if (f && isOnline()) {
      void runCardOcr(f);
    } else {
      setCardOcrError(null);
      setOcrAppliedFields([]);
      setOcrConfidence(null);
      setOcrBannerDismissed(false);
    }
  }

  // Sprint 22 + Sprint 40 · OCR-Call. Zeigt Result-Modal statt silent-auto-fill.
  // Async/non-blocking — User kann bereits parallel weiterarbeiten.
  async function runCardOcr(f: File): Promise<void> {
    setCardOcrBusy(true);
    setCardOcrError(null);
    setOcrAppliedFields([]);
    setOcrConfidence(null);
    setOcrResult(null);
    setOcrBannerDismissed(false);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const res = await fetch('/api/leads/ocr-card', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: fd,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error((detail as { detail?: string }).detail ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as OcrResponse;
      setOcrResult(data);  // Modal öffnet sich automatisch
      setOcrConfidence(typeof data.confidence === 'number' ? data.confidence : null);
    } catch (err) {
      setCardOcrError((err as Error).message ?? 'OCR fehlgeschlagen');
    } finally {
      setCardOcrBusy(false);
    }
  }

  // Sprint 40 + Sprint 41 · User akzeptiert nur die ausgewählten Felder
  // (selectedKeys = Set von OCR-Feld-Keys die im Modal aktiviert sind)
  function applyOcrResult(selectedKeys: Set<string>): void {
    if (!ocrResult) return;
    const extracted = ocrResult.extracted ?? {};
    const applied: string[] = [];
    setValues((prev) => {
      const next = { ...prev };
      for (const [ocrKey, fieldId] of Object.entries(OCR_FIELD_MAP)) {
        if (!selectedKeys.has(ocrKey)) continue;
        const val = extracted[ocrKey];
        if (typeof val === 'string' && val.trim() !== '') {
          // Don't overwrite values user has already typed
          if (typeof next[fieldId] !== 'string' || (next[fieldId] as string).trim() === '') {
            next[fieldId] = val.trim();
            applied.push(fieldId);
          }
        }
      }
      return next;
    });
    setOcrAppliedFields(applied);
    setOcrResult(null);
  }

  function clearCard() {
    if (cardPreview) URL.revokeObjectURL(cardPreview);
    setCardFile(null);
    setCardPreview(null);
    if (cardInputRef.current) cardInputRef.current.value = '';
    // Sprint 22 · OCR-State auch zurücksetzen
    setCardOcrBusy(false);
    setCardOcrError(null);
    setOcrAppliedFields([]);
    setOcrConfidence(null);
    setOcrBannerDismissed(false);
  }

  // Sprint 19 (S19.2) · Anhänge werden inline unter Notiz-Textareas hinzugefügt.
  // Single-file-add via VoiceMagicTextarea +Anhang-Button; mehrfach möglich.
  function addLocalAttachment(file: File): void {
    const isImage = file.type.startsWith('image/');
    setExtras((prev) => [
      ...prev,
      {
        file,
        kind: isImage ? 'foto' : 'dokument',
        preview: isImage ? URL.createObjectURL(file) : null,
      },
    ]);
  }

  function removeExtra(idx: number) {
    setExtras((prev) => {
      const removed = prev[idx];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== idx);
    });
  }

  function buildPayload() {
    if (!form) return null;
    // Sprint 17 (S17.1) · Bug-Fix: AssigneePicker speichert UUID unter dem
    // Field-id (z.B. "uebernehmender") in `values`. Backend erwartet diese
    // UUID im top-level `uebernehmender_id` (Pydantic LeadCreate). Vorher
    // wurde das nur im `data`-Blob versendet und beim Save weggeworfen.
    // Wir mappen jedes `assignee`-Feld → `uebernehmender_id` (erstes
    // gefundenes Feld gewinnt — Default-Form hat genau eines).
    let uebernehmender: string | undefined;
    for (const f of form.schema.fields) {
      if (f.type === 'assignee') {
        const v = values[f.id];
        if (typeof v === 'string' && v.trim() !== '') {
          uebernehmender = v.trim();
          break;
        }
      }
    }
    return {
      form_id: form.id,
      name: pickStr(values, 'name'),
      firma: pickStr(values, 'firma'),
      email: pickStr(values, 'email') || undefined,
      telefon: pickStr(values, 'telefon'),
      // Sprint 63 · mobil als Top-Level-Payload zum Backend
      mobil: pickStr(values, 'mobil'),
      potenzial: typeof values.potenzial === 'number' ? values.potenzial : undefined,
      messetag:
        typeof values.messetag === 'string' && values.messetag !== ''
          ? values.messetag
          : undefined,
      uebernehmender_id: uebernehmender,
      data: values,
    };
  }

  function resetForReNew() {
    setCreatedLead(null);
    setQueuedNotice(null);
    setError(null);
    setMagicApplied(false);
    if (form) {
      const initial: LeadFormValues = {};
      for (const field of form.schema.fields) {
        if (field.type === 'boolean') initial[field.id] = field.default ?? false;
      }
      setValues(initial);
    } else {
      setValues({});
    }
    clearCard();
    extras.forEach((e) => e.preview && URL.revokeObjectURL(e.preview));
    setExtras([]);
  }

  async function tryEnqueue(payload: Record<string, unknown>): Promise<string | null> {
    try {
      // Offline-Outbox supportet aktuell nur 1 Visitenkarte (Block 9, UNITI Phase 1)
      // Extras werden im Offline-Pfad ignoriert — das wird im Frontend kommuniziert.
      return await enqueueLead(payload, cardFile);
    } catch (err) {
      setError((err as Error).message ?? 'Offline-Speicher voll');
      return null;
    }
  }

  async function uploadExtras(leadId: string): Promise<void> {
    for (const att of extras) {
      const fd = new FormData();
      fd.append('file', att.file);
      fd.append('kind', att.kind);
      try {
        await fetch(`/api/leads/${leadId}/attachments`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Authorization: `Bearer ${getAccessToken() ?? ''}`,
          },
          body: fd,
        });
      } catch {
        // Single-attachment-fail soll Lead-Save nicht blockieren — best-effort
      }
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!form) return;
    setError(null);
    setQueuedNotice(null);
    setBusy(true);

    const payload = buildPayload();
    if (!payload) return;

    if (!isOnline()) {
      const id = await tryEnqueue(payload);
      if (id) {
        const extraNote = extras.length > 0 ? ` (${extras.length} Anhang/Anhänge im Offline-Pfad ausgelassen)` : '';
        setQueuedNotice(
          `Offline gespeichert · ${id.slice(-8)} · synct beim nächsten Online-Event${extraNote}`,
        );
        setValues({});
        clearCard();
        extras.forEach((ext) => ext.preview && URL.revokeObjectURL(ext.preview));
        setExtras([]);
      }
      setBusy(false);
      return;
    }

    try {
      const lead = await api<CreatedLead>('/api/leads', {
        method: 'POST',
        body: payload,
      });
      // Visitenkarte zuerst (legacy /visitenkarte path für OCR-Mirroring)
      if (cardFile) {
        const fd = new FormData();
        fd.append('file', cardFile);
        await fetch(`/api/leads/${lead.id}/visitenkarte`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Authorization: `Bearer ${getAccessToken() ?? ''}`,
          },
          body: fd,
        });
        // Auch als Sprint-10-Anhang persistieren — Lead-Detail listet sie auf
        const fdAtt = new FormData();
        fdAtt.append('file', cardFile);
        fdAtt.append('kind', 'visitenkarte');
        await fetch(`/api/leads/${lead.id}/attachments`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Authorization: `Bearer ${getAccessToken() ?? ''}`,
          },
          body: fdAtt,
        });
      }
      if (extras.length > 0) {
        await uploadExtras(lead.id);
      }
      setCreatedLead(lead);
      setValues({});
      clearCard();
      extras.forEach((ext) => ext.preview && URL.revokeObjectURL(ext.preview));
      setExtras([]);
    } catch (err) {
      const e = err as { detail?: string; status?: number };
      // Network-failure (kein status) oder 5xx → enqueue für offline-sync
      if (e.status === undefined || (e.status >= 500 && e.status < 600)) {
        const id = await tryEnqueue(payload);
        if (id) {
          setQueuedNotice(
            `Server nicht erreichbar · gespeichert ${id.slice(-8)} · synct automatisch`,
          );
          setValues({});
          clearCard();
          extras.forEach((ext) => ext.preview && URL.revokeObjectURL(ext.preview));
          setExtras([]);
        }
      } else {
        setError(e.detail ?? 'Konnte Lead nicht speichern.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!user) return null;
  if (error && !form) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title auth-title--small">FEHLER</h1>
          <p className="auth-error">{error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <AppHeader subtitle="ERFASSUNG" showOnlineBadge backTo="/dashboard" />
      <section className="app-shell__main">
        <div className="app-shell__card capture-card">
          <div className="app-shell__card-label">
            FORMULAR · {form?.name ?? '…'} · v{form?.version ?? '?'}
          </div>
          <h1 className="app-shell__card-title">{form?.schema.title ?? 'Lade…'}</h1>
          {form?.schema.description ? (
            <p className="app-shell__card-text">{form.schema.description}</p>
          ) : null}

          {createdLead ? (
            <div className="capture-success">
              <p className="auth-success">
                Lead gespeichert · ID {createdLead.id.slice(0, 8)}…
              </p>
              <button className="auth-submit" onClick={resetForReNew}>
                NEUEN LEAD ERFASSEN
              </button>
            </div>
          ) : null}

          {queuedNotice ? (
            <div className="capture-success">
              <p className="auth-success">{queuedNotice}</p>
              <button className="auth-submit" onClick={resetForReNew}>
                NEUEN LEAD ERFASSEN
              </button>
            </div>
          ) : null}

          {!createdLead && !queuedNotice && form ? (
            <form onSubmit={handleSubmit} className="auth-form capture-form">
              {/* Sprint 18 (S18.2) · Offline-Hinweis oben am Formular */}
              {!online ? (
                <Hint variant="warning" icon={WifiOff}>
                  OFFLINE · Lead wird gespeichert · synct beim nächsten Online-Event
                </Hint>
              ) : null}
              {/* Visitenkarte · mockup-konform: Big-Camera-Button */}
              <div className="capture-card-block">
                <label className="form-field__label" htmlFor="visitenkarte">
                  VISITENKARTE · FOTO
                </label>
                <input
                  id="visitenkarte"
                  ref={cardInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  capture="environment"
                  onChange={handleCardChange}
                  style={{ display: 'none' }}
                />
                {!cardPreview ? (
                  <>
                    <button
                      type="button"
                      className="capture-card-button capture-card-button--primary"
                      onClick={() => cardInputRef.current?.click()}
                      aria-label="Visitenkarte fotografieren"
                    >
                      <Camera size={28} strokeWidth={2} className="capture-card-button__icon" />
                      <span className="capture-card-button__label">Visitenkarte fotografieren</span>
                    </button>
                    <div className="capture-card-sparkle-hint">
                      <Sparkles size={14} />
                      <span>Felder werden automatisch ausgefüllt</span>
                    </div>
                  </>
                ) : (
                  <div className="capture-card-preview">
                    <div className="capture-card-preview-image-wrap">
                      <img src={cardPreview} alt="Visitenkarten-Vorschau" />
                      {/* Sprint 22 (S22.1) · Loading-Overlay während OCR läuft */}
                      {cardOcrBusy ? (
                        <div className="capture-card-ocr-overlay" role="status" aria-live="polite">
                          <Sparkles size={20} className="capture-card-ocr-spinner" />
                          <span>Visitenkarte wird ausgewertet…</span>
                        </div>
                      ) : null}
                    </div>
                    <div className="capture-card-preview-actions">
                      <button
                        type="button"
                        className="app-shell__logout"
                        onClick={clearCard}
                        disabled={cardOcrBusy}
                      >
                        <XIcon size={14} /> Verwerfen
                      </button>
                      <button
                        type="button"
                        className="app-shell__logout"
                        onClick={() => cardInputRef.current?.click()}
                        disabled={cardOcrBusy}
                      >
                        <Camera size={14} /> Neu
                      </button>
                    </div>
                    <p className="form-field__hint">
                      {cardFile?.name ?? 'Visitenkarte'} · wird beim Speichern hochgeladen
                    </p>
                  </div>
                )}
                {/* Sprint 22 (S22.2) · OCR-Result-Banner */}
                {!cardOcrBusy && ocrAppliedFields.length > 0 && !ocrBannerDismissed ? (
                  <div className="ocr-result-banner" role="status">
                    <Sparkles size={16} className="ocr-result-banner__icon" aria-hidden="true" />
                    <span className="ocr-result-banner__text">
                      {ocrAppliedFields.length}{' '}
                      {ocrAppliedFields.length === 1 ? 'Feld' : 'Felder'} automatisch ausgefüllt
                      {ocrConfidence !== null
                        ? ` · ${Math.round(ocrConfidence * 100)}% Genauigkeit`
                        : ''}{' '}
                      · bitte prüfen
                    </span>
                    <button
                      type="button"
                      className="ocr-result-banner__close"
                      onClick={() => setOcrBannerDismissed(true)}
                      aria-label="Hinweis schließen"
                    >
                      <XIcon size={14} />
                    </button>
                  </div>
                ) : null}
                {/* Sprint 22 (S22.1) · OCR-Error-Hint */}
                {!cardOcrBusy && cardOcrError ? (
                  <Hint variant="warning">
                    OCR fehlgeschlagen — Felder bitte manuell ausfüllen
                  </Hint>
                ) : null}
              </div>

              {/* Sprint 19 (S19.2) · Anhänge erscheinen inline unter Notiz-Textareas
                  durch VoiceMagicTextarea — kein eigener Block mehr. */}

              <FormRenderer
                fields={form.schema.fields}
                values={values}
                onChange={handleFormChange}
                onMagicAccepted={handleMagicAccepted}
                ocrAppliedFields={ocrAppliedFields}
                localAttachments={memoizedLocalAttachments}
                onAddLocalAttachment={addLocalAttachment}
                onRemoveLocalAttachment={removeExtra}
              />

              {error ? (
                <div className="auth-error" role="alert">
                  {error}
                </div>
              ) : null}

              {/* Sprint 18 (S18.4) · Sparkles-Icon im Save-Button wenn KI-Strukturiert übernommen */}
              <button type="submit" disabled={busy} className="auth-submit capture-submit">
                {magicApplied && !busy ? <Sparkles size={16} aria-hidden="true" /> : null}
                <span>{busy ? 'WIRD GESPEICHERT…' : 'LEAD SPEICHERN'}</span>
              </button>
            </form>
          ) : null}
        </div>
      </section>

      {/* Sprint 40 · OCR-Result-Modal · Mockup screen-20 */}
      {ocrResult && (
        <OcrResultModal
          result={ocrResult}
          cardPreview={cardPreview}
          onAccept={applyOcrResult}
          onDismiss={() => setOcrResult(null)}
        />
      )}
    </main>
  );
}

function OcrResultModal({
  result,
  cardPreview,
  onAccept,
  onDismiss,
}: {
  result: OcrResponse;
  cardPreview: string | null;
  onAccept: (selectedKeys: Set<string>) => void;
  onDismiss: () => void;
}) {
  const fields = Object.entries(result.extracted).filter(
    ([, v]) => typeof v === 'string' && v.trim() !== '',
  );
  // Sprint 41 · Default-Selection: Felder mit confidence ≥ 80% sind initial
  // ausgewählt. User toggelt per Click pro Feld.
  const [selected, setSelected] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const [key] of fields) {
      const conf = result.field_confidences?.[key] ?? 0;
      if (conf >= 0.8) initial.add(key);
    }
    return initial;
  });

  function toggle(key: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function confLabel(conf: number): { text: string; cls: string } {
    if (conf >= 0.9) return { text: 'sehr sicher', cls: 'ocr-conf--high' };
    if (conf >= 0.7) return { text: 'sicher', cls: 'ocr-conf--mid' };
    if (conf >= 0.4) return { text: 'unsicher', cls: 'ocr-conf--low' };
    return { text: 'sehr unsicher', cls: 'ocr-conf--very-low' };
  }

  return (
    <div className="modal-overlay" onClick={onDismiss}>
      <div className="modal-card ocr-result-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ocr-result-modal__head">
          <Sparkles size={18} className="ocr-result-modal__head-icon" aria-hidden />
          <div>
            <h2 className="app-shell__card-title" style={{ margin: 0 }}>
              Erkannte Felder
            </h2>
            <p className="app-shell__card-meta" style={{ margin: '4px 0 0' }}>
              {fields.length === 0
                ? 'Keine Felder erkannt'
                : `${fields.length} Feld${fields.length === 1 ? '' : 'er'} erkannt · ${selected.size} ausgewählt · Tippen zum An/Abwählen`}
            </p>
          </div>
        </div>

        {cardPreview ? (
          <div className="ocr-result-modal__preview">
            <img src={cardPreview} alt="Visitenkarte" />
          </div>
        ) : null}

        {fields.length === 0 ? (
          <div className="ocr-result-empty">
            Es wurden keine Felder zuverlässig erkannt. Bitte prüfe das Foto
            (Beleuchtung, Schärfe) oder fülle die Felder manuell aus.
          </div>
        ) : (
          <ul className="ocr-result-list" aria-label="Erkannte Felder · Tippen zum Auswählen">
            {fields.map(([key, val]) => {
              const conf = result.field_confidences?.[key] ?? 0;
              const c = confLabel(conf);
              const label = OCR_FIELD_LABELS[key] ?? key;
              const isSelected = selected.has(key);
              return (
                <li key={key}>
                  <button
                    type="button"
                    className={`ocr-result-item ocr-result-item--clickable ${
                      isSelected ? 'ocr-result-item--selected' : ''
                    }`}
                    onClick={() => toggle(key)}
                    aria-pressed={isSelected}
                  >
                    <div className="ocr-result-item__row">
                      <div className="ocr-result-item__label">{label.toUpperCase()}</div>
                      {isSelected ? (
                        <span className="ocr-result-item__check" aria-hidden>✓</span>
                      ) : null}
                    </div>
                    <div className="ocr-result-item__value">{val}</div>
                    <div className={`ocr-result-item__conf ${c.cls}`}>
                      {Math.round(conf * 100)}% · {c.text}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="ocr-result-modal__actions">
          {fields.length > 0 && (
            <button
              type="button"
              className="auth-submit"
              onClick={() => onAccept(selected)}
              disabled={selected.size === 0}
            >
              <Sparkles size={14} aria-hidden />
              {selected.size === 0
                ? 'NICHTS AUSGEWÄHLT'
                : `${selected.size} ÜBERNEHMEN`}
            </button>
          )}
          <button type="button" className="app-shell__logout" onClick={onDismiss}>
            {fields.length > 0 ? 'ABBRECHEN' : 'SCHLIESSEN'}
          </button>
        </div>
      </div>
    </div>
  );
}

function pickStr(values: LeadFormValues, key: string): string | undefined {
  const v = values[key];
  if (typeof v === 'string' && v.trim() !== '') return v.trim();
  return undefined;
}
